import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ASSETS } from "@/lib/assets";

const NAV_LINKS = [
  { label: "Ambassadors", href: "#ambassadors", highlight: true },
  { label: "Meet Fifi", href: "#meet-fifi" },
  { label: "Quant Agent", href: "#fifi-quant" },
  { label: "PawFolio", href: "#pawfolio" },
  { label: "Simulation", href: "#simulation" },
  { label: "Inside App", href: "#app-showcase" },
  { label: "Master Investors", href: "#masters" },
  { label: "Rewards", href: "#fifi-store" },
  { label: "FAQ", href: "#faq" },
];

const FinXLogo = () => (
  <a href="#top" data-testid="navbar-logo" className="flex items-center gap-2.5">
    <img
      src={ASSETS.pawIcon}
      alt="FinX paw"
      className="h-8 w-8 object-contain"
    />
    <img
      src={ASSETS.wordmark}
      alt="FinX"
      className="h-6 w-auto object-contain"
    />
    <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-[#EDE9FE] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#5B21B6]">
      Beta
    </span>
  </a>
);

const Navbar = ({ onWaitlistClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`sticky top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/70 border-b border-slate-200/60"
          : "bg-white/60 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between gap-3">
        <FinXLogo />

        <nav aria-label="Main navigation" className="hidden xl:flex items-center gap-0.5">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-2 py-2 text-sm whitespace-nowrap font-semibold transition-colors rounded-full ${
                link.highlight
                  ? "text-[#5B21B6] bg-[#F5F3FF] hover:bg-[#EDE9FE]"
                  : "text-slate-700 hover:text-[#5B21B6] hover:bg-[#F5F3FF]"
              }`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex shrink-0 items-center gap-3">
          <Button
            data-testid="navbar-cta-waitlist"
            onClick={onWaitlistClick}
            className="hidden sm:inline-flex rounded-full px-5 py-2 h-10 font-bold text-white brand-gradient hover:opacity-95 transition-all hover:-translate-y-0.5 border-0 shadow-[0_8px_24px_-8px_rgba(168,85,247,0.6)]"
          >
            Join Waitlist
          </Button>
          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen((s) => !s)}
            className="xl:hidden p-2 rounded-full text-slate-700 hover:bg-slate-100"
            aria-label="Toggle menu"
            aria-expanded={open}
            aria-controls="navbar-mobile-panel"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {open && (
        <div
          id="navbar-mobile-panel"
          data-testid="navbar-mobile-panel"
          className="xl:hidden max-h-[calc(100dvh-4rem)] overflow-y-auto bg-white/95 backdrop-blur-xl border-b border-slate-200"
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`navbar-mobile-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setOpen(false)}
                className="px-3 py-2.5 rounded-xl text-base font-semibold text-slate-800 hover:bg-[#F5F3FF]"
              >
                {link.label}
              </a>
            ))}
            <Button
              data-testid="navbar-mobile-cta"
              onClick={() => {
                setOpen(false);
                onWaitlistClick?.();
              }}
              className="mt-2 rounded-full font-bold text-white brand-gradient border-0"
            >
              Join Waitlist
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
